import AbstractService, {
  ServicePartner
} from "./AbstractService";

/**
 * a single file a supplier provides to the logged in partner
 */
export interface PartnerFile {
  path: string;
  filename: string;
  frontendPath: string;
  size: number;
  lastModified: number;
  fileId: string;
  private: boolean;
}

/**
 * files of all suppliers, keyed by supplier partner id
 */
export interface PartnerFileGroups {
  [supplierId: string]: PartnerFile[];
}

/**
 * temporary download link for a single file
 */
export interface PartnerFileDownload {
  url: string;
  expiresAt: string;
  fileName: string;
}

/**
 * one release of the logged in partner's own files: either to every customer
 * (public, without a customer id) or to a single one (private)
 */
export interface ProvidedFileGroup {
  visibility: 'public' | 'private';
  customerId: string | null;
  files: PartnerFile[];
}

/**
 * everything the logged in partner provides as a supplier, plus what it costs
 * of its storage
 */
export interface ProvidedFiles {
  quota: {
    used: number;
    max: number;
  };
  groups: ProvidedFileGroup[];
}

/**
 * PartnerFileService
 */
export default class PartnerFileService extends AbstractService {
  /**
   * ServicePartner
   */
  static service: ServicePartner;

  /**
   * constructor
   */
  constructor() {
    super();

    /**
     * the api package has no partnerFile service yet, so the endpoints are
     * called through the shared http service of an existing one
     */
    PartnerFileService.service = this.getApi().getServicePartner();
  }

  /**
   * getFiles
   * files of every supplier that has the module active for this partner
   */
  getFiles(): Promise<PartnerFileGroups> {
    return PartnerFileService.service.getHttpService()
      .get('/partnerfiles')
      .then((response) => {
        /**
         * without a single active supplier the backend serializes an empty
         * php array, which arrives as [] instead of {}
         */
        let data = response.data;

        return (data && !Array.isArray(data)) ? data as PartnerFileGroups : {};
      });
  }

  /**
   * getProvidedFiles
   * the files the logged in partner provides to its own customers, grouped by
   * who may see them, together with its storage consumption
   */
  getProvidedFiles(): Promise<ProvidedFiles> {
    return PartnerFileService.service.getHttpService()
      .get('/partnerfiles/provided')
      .then((response) => {
        /**
         * every part of the answer is treated as optional: a partner without
         * storage of its own reaches this endpoint as well, and a php array
         * without entries again arrives as [] instead of {}
         */
        const data = (response.data || {}) as Partial<ProvidedFiles>,
          quota = data.quota || { used: 0, max: 0 },
          groups = Array.isArray(data.groups) ? data.groups : [];

        const result: ProvidedFiles = {
          quota: {
            used: Number(quota.used) || 0,
            max: Number(quota.max) || 0
          },
          groups: groups.map((group): ProvidedFileGroup => ({
            /**
             * public is the fallback on purpose: a group whose visibility got
             * lost still belongs to the release it was delivered in, and
             * "private" would claim a customer that is not there
             */
            visibility: group && group.visibility === 'private' ? 'private' : 'public',
            customerId: (group && group.customerId) || null,
            files: (group && Array.isArray(group.files)) ? group.files : []
          }))
        };

        return result;
      });
  }

  /**
   * getDownloadUrl
   * the endpoint answers with the presigned storage url as json, because a
   * redirect cannot be followed from another origin with a bearer token
   * @param fileId
   */
  getDownloadUrl(fileId: string): Promise<PartnerFileDownload> {
    return PartnerFileService.service.getHttpService()
      .get('/partnerfiles/downloadUrl/' + encodeURIComponent(fileId))
      .then((response) => response.data as PartnerFileDownload);
  }
}
